import React from 'react';
import { getColorClass, PALETTES } from '../utils/colorUtils';
import useConfig from '../config';

// Define Course interface directly within the component
interface Course {
  name: string;
  slots: string[];
  credits: number;
  colorIndex: number;
  facultyPreferences?: string[];
  facultyLabAssignments?: Array<{ facultyName: string; slots: string[] }>;
  code?: string;
  faculty?: string;
  venue?: string;
}

interface SlotObject {
  colspan: number;
  content: string;
}

type Slot = string | SlotObject;

interface DayRow {
  day: string;
  slots: Slot[];
}

interface TimeTableProps {
  courses?: Course[];
  darkMode?: boolean;
  palette?: keyof typeof PALETTES;
  // New props for interactive mode
  isSelectMode?: boolean;
  onCellClick?: (event: React.MouseEvent<HTMLButtonElement>, cellSlots: string) => void;
  selectedSlots?: string[];
  existingSlots?: string[];
  getCellInteractionClass?: (cellSlots: string, selectedSlots: string[], existingSlots: string[]) => string;
  hideContentForCell?: string;
}

const TimeTable: React.FC<TimeTableProps> = ({ 
  courses = [], 
  darkMode = false,
  palette = 'default',
  isSelectMode = false,
  onCellClick,
  selectedSlots = [],
  existingSlots = [],
  getCellInteractionClass,
  hideContentForCell
}) => {
  const config = useConfig();
  const { dayRows, theoryTimes, labTimes } = config;

  // Get course initials (first letter of each word)
  const getCourseInitials = (name: string): string => {
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .join('');
  };

  // Helper function to get base course name (e.g., "Course A" from "Course A Lab")
  const getBaseCourseName = (name: string) => name.replace(/ Lab$/, '');

  // Find course that includes a specific slot
  const findCourseForSlot = (slotText: string): { course: Course; index: number; matchedSlotPart: string } | null => {
    const timetableSlotIdentifiers = slotText.split('/'); // e.g., ["A1", "L1"] or ["L6"]

    for (let i = 0; i < courses.length; i++) {
      const course = courses[i];
      let courseEffectiveSlots: string[] = [...course.slots]; // Default to general course slots

      if (course.name.endsWith(' Lab') &&
          course.facultyPreferences && course.facultyPreferences.length > 0 &&
          course.facultyLabAssignments) {
        const firstFacultyName = course.facultyPreferences[0];
        const firstFacultyLabAssignment = course.facultyLabAssignments.find(a => a.facultyName === firstFacultyName);
        
        if (firstFacultyLabAssignment && firstFacultyLabAssignment.slots.length > 0) {
          courseEffectiveSlots = firstFacultyLabAssignment.slots; 
        }
      }

      for (const ttSlotId of timetableSlotIdentifiers) {
        if (courseEffectiveSlots.includes(ttSlotId)) {
          return { course, index: i, matchedSlotPart: ttSlotId };
        }
      }
    }
    return null;
  };

  // Create theory and lab time arrays without lunch row
  // const theoryTimes = [
  //   "08:00AM\nto\n08:50AM",
  //   "09:00AM\nto\n09:50AM",
  //   "10:00AM\nto\n10:50AM",
  //   "11:00AM\nto\n11:50AM",
  //   "12:00AM\nto\n12:50PM",
  //   "",
  //   "02:00PM\nto\n02:50PM",
  //   "03:00PM\nto\n03:50PM",
  //   "04:00PM\nto\n04:50PM",
  //   "05:00PM\nto\n05:50PM",
  //   "06:00PM\nto\n06:50PM",
  //   ""
  // ];

  // const labTimes = [
  //   "08:00AM\nto\n08:50AM",
  //   "08:51AM\nto\n09:40AM",
  //   "09:51AM\nto\n10:40AM",
  //   "10:41AM\nto\n11:30AM",
  //   "11:40AM\nto\n12:30PM",
  //   "12:31PM\nto\n01:20PM",
  //   "02:00PM\nto\n02:50PM",
  //   "02:51PM\nto\n03:40PM",
  //   "03:51PM\nto\n04:40PM",
  //   "04:41PM\nto\n05:30PM",
  //   "05:40PM\nto\n06:30PM",
  //   "06:31PM\nto\n07:20PM"
  // ];


  const cellStyle = {
    width: '80px',
    height: '80px',
    minWidth: '80px',
    minHeight: '80px',
    padding: 0 // Remove padding for button to fill cell
  };
  
  const headerCellStyle = { // Original style for header cells
    width: '80px',
    height: '80px',
    minWidth: '80px',
    minHeight: '80px'
  };

  const renderSlotCell = (slotItem: Slot, key: string | number) => {
    if (typeof slotItem === 'object' && 'colspan' in slotItem) {
      return (
        <td 
          key={key} 
          colSpan={slotItem.colspan}
          className={`px-4 py-3 text-xs text-center border-r ${darkMode ? 'border-gray-700' : 'border-gray-200'}`}
          style={{ height: '80px' }}
        >
          <div className={`${darkMode ? 'text-red-500' : 'text-red-600'} text-sm font-medium`}>
            {slotItem.content}
          </div>
        </td>
      );
    }

    // Ensure slotItem is a string for further processing
    if (typeof slotItem !== 'string') return <td key={key} style={cellStyle} />;

    const courseInfo = findCourseForSlot(slotItem);
    const interactionClass = isSelectMode && getCellInteractionClass ? getCellInteractionClass(slotItem, selectedSlots, existingSlots) : '';
    const isDisabled = isSelectMode && slotItem.split('/').every(s => existingSlots.includes(s));
    const colorClass = courseInfo ? PALETTES[palette].colors[courseInfo.course.colorIndex] : '';

    return (
      <td 
        key={key} 
        className={`border-r ${darkMode ? 'border-gray-700' : 'border-gray-200'} ${!isSelectMode ? colorClass : ''} ${isSelectMode ? 'p-0' : 'px-4 py-3'}`}
        style={isSelectMode ? cellStyle : headerCellStyle} 
      >
        {isSelectMode ? ((() => {
          const isThisCellSelected = !!courseInfo;
          const isCourseNameEntered = isThisCellSelected && courseInfo.course.name && courseInfo.course.name !== 'Selecting...';
          const shouldHideThisCellContent = hideContentForCell === slotItem;

          let dynamicButtonClasses = interactionClass;
          if (!shouldHideThisCellContent) {
            if (isThisCellSelected) {
              dynamicButtonClasses += ` ${colorClass} flex-col`;
            } else {
              dynamicButtonClasses += darkMode ? ' text-gray-400' : ' text-gray-500';
            }
          }

          return (
            <button
              className={`w-full h-full text-xs text-center flex items-center justify-center ${dynamicButtonClasses}`}
              onClick={(e: React.MouseEvent<HTMLButtonElement>) => onCellClick && onCellClick(e, slotItem)}
              disabled={isDisabled}
            >
              {shouldHideThisCellContent ? (
                '\u00A0'
              ) : isThisCellSelected ? (
                <>
                  {isCourseNameEntered && (
                    <div className="font-bold">{getCourseInitials(courseInfo.course.name)}</div>
                  )}
                  <div className={`text-xs ${isCourseNameEntered ? 'mt-1' : ''}`}>
                    {slotItem.includes('/') ? (
                      slotItem.split('/').map((part: string, i: number, arr: string[]) => (
                        <React.Fragment key={i}>
                          <span className={part === courseInfo.matchedSlotPart ? (part.length > 3 ? 'text-sm font-semibold' : 'text-base font-semibold') : 'text-[11px]'}>
                            {part}
                          </span>
                          {i < arr.length - 1 && '/'}
                        </React.Fragment>
                      ))
                    ) : (
                      <span className={slotItem === courseInfo.matchedSlotPart ? (slotItem.length > 3 ? 'text-sm font-semibold' : 'text-base font-semibold') : 'text-[11px]'}>
                        {slotItem}
                      </span>
                    )}
                  </div>
                </>
              ) : (
                slotItem // Not selected, not hidden: show plain slotItem
              )}
            </button>
          );
        })()) : courseInfo ? (
          <div className="flex flex-col items-center justify-center h-full w-full">
            <div className="font-bold">{getCourseInitials(courseInfo.course.name)}</div>
            <div className="text-xs mt-1">
              {slotItem.includes('/') ? (
                slotItem.split('/').map((part: string, i: number, arr: string[]) => (
                  <React.Fragment key={i}>
                    <span className={part === courseInfo.matchedSlotPart ? (part.length > 3 ? 'text-sm' : 'text-base') : 'text-[11px]'}>
                      {part}
                    </span>
                    {i < arr.length - 1 && '/'}
                  </React.Fragment>
                ))
              ) : (
                <span className={slotItem === courseInfo.matchedSlotPart ? (slotItem.length > 3 ? 'text-sm' : 'text-base') : ''}>
                  {slotItem}
                </span>
              )}
            </div>
          </div>
        ) : (
          <div className={`text-xs text-center flex items-center justify-center h-full w-full ${darkMode ? 'text-gray-500' : 'text-gray-600'}`}>{slotItem}</div>
        )}
      </td>
    );
  };

  return (
    <div className={`overflow-x-auto rounded-xl timetable-container`}>
      <table className={`min-w-full table-fixed ${darkMode ? 'bg-gray-800' : 'bg-white'}`}>
        <tbody className={`divide-y ${darkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
          {/* Theory Hours Row */}
          <tr className={darkMode ? 'bg-gray-700/80' : 'bg-gray-50'}>
            <td className={`px-4 py-3 text-sm font-medium border-r ${darkMode ? 'text-gray-300 border-gray-700' : 'text-gray-900 border-gray-200'}`} style={{ width: '80px' }}>
              THEORY<br />HOURS
            </td>
            {theoryTimes.slice(0, 6).map((time, index) => (
              <td key={index} className={`px-4 py-3 text-xs font-semibold text-center border-r ${darkMode ? 'text-gray-300 border-gray-700' : 'text-gray-900 border-gray-200'}`} style={headerCellStyle}>
                {time.split("\n").map((t, i) => (
                  <React.Fragment key={i}>
                    {t}
                    {i !== 2 && time !== "" && <br />}
                  </React.Fragment>
                ))}
              </td>
            ))}
            
            {/* Lunch column - appears only once */}
            <td 
              rowSpan={7} 
              className={`font-semibold text-center align-middle ${darkMode ? 'bg-gray-700/80 text-gray-300' : 'bg-gray-50 text-gray-900'}`}
              style={{
                width: '40px',
                minWidth: '40px',
                borderLeft: '1px solid #e5e7eb',
                borderRight: '1px solid #e5e7eb',
                background: darkMode ? 'rgba(55, 65, 81, 0.8)' : '#f9fafb',
                position: 'relative',
                boxShadow: 'none'
              }}
            >
              <div 
                className="flex flex-col items-center justify-center h-full space-y-2"
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  background: 'inherit'
                }}
              >
                {'LUNCH'.split('').map((char, index) => (
                  <span key={index} className="relative z-10">{char}</span>
                ))}
              </div>
            </td>

            {theoryTimes.slice(6).map((time, index) => (
              <td key={index + 6} className={`px-4 py-3 text-xs font-semibold text-center border-r ${darkMode ? 'text-gray-300 border-gray-700' : 'text-gray-900 border-gray-200'}`} style={headerCellStyle}>
                {time.split("\n").map((t, i) => (
                  <React.Fragment key={i}>
                    {t}
                    {i !== 2 && time !== "" && <br />}
                  </React.Fragment>
                ))}
              </td>
            ))}
          </tr>

          {/* Lab Hours Row */}
          <tr className={darkMode ? 'bg-gray-700/80' : 'bg-gray-50'}>
            <td className={`px-4 py-3 text-sm font-medium border-r ${darkMode ? 'text-gray-300 border-gray-700' : 'text-gray-900 border-gray-200'}`} style={{ width: '80px' }}>
              LAB<br />HOURS
            </td>
            {labTimes.slice(0, 6).map((time, index) => (
              <td key={index} className={`px-4 py-3 text-xs font-semibold text-center border-r ${darkMode ? 'text-gray-300 border-gray-700' : 'text-gray-900 border-gray-200'}`} style={headerCellStyle}>
                {time.split("\n").map((t, i) => (
                  <React.Fragment key={i}>
                    {t}
                    {i !== 2 && <br />}
                  </React.Fragment>
                ))}
              </td>
            ))}
            
            {/* Lunch column is handled by rowSpan from Theory row */}

            {labTimes.slice(6).map((time, index) => (
              <td key={index + 6} className={`px-4 py-3 text-xs font-semibold text-center border-r ${darkMode ? 'text-gray-300 border-gray-700' : 'text-gray-900 border-gray-200'}`} style={headerCellStyle}>
                {time.split("\n").map((t, i) => (
                  <React.Fragment key={i}>
                    {t}
                    {i !== 2 && <br />}
                  </React.Fragment>
                ))}
              </td>
            ))}
          </tr>

          {/* Days and Periods */}
          {dayRows.map(({ day, slots: dayColumnSlots }) => ( // Renamed slots to dayColumnSlots to avoid conflict
            <tr key={day} className={`transition-colors`}>
              <td className={`px-4 py-3 text-sm font-medium border-r ${darkMode ? 'text-gray-300 border-gray-700 bg-gray-700/80' : 'text-gray-900 border-gray-200 bg-gray-50'}`} style={{ width: '80px' }}>
                {day}
              </td>
              
              {/* First half of slots (before lunch) */}
              {dayColumnSlots.slice(0, 6).map((slotItem, index) => renderSlotCell(slotItem, `${day}-am-${index}`))}
              
              {/* No lunch cell here - it's handled by the rowSpan in the header */}
              
              {/* Second half of slots (after lunch) */}
              {dayColumnSlots.slice(6).map((slotItem, index) => renderSlotCell(slotItem, `${day}-pm-${index}`))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default TimeTable; 
